import type { IntelligenceTopicDTO } from '@conversed/rest/src/contracts/home/home.dto';
// * SENTIMENT_VALUES follows the enum order - unhappy, neutral, happy - which is
// * also the left-to-right order of the bar and its counts.
import {
  SENTIMENT_VALUES,
  type Sentiment,
} from '@conversed/shared/src/central/constants/sentiment';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { SENTIMENT_DISPLAY } from '../sentimentDisplay';

type SentimentBuckets = IntelligenceTopicDTO['sentiment'];

type SentimentSplitBarProps = {
  sentiment: SentimentBuckets;
  // When set, the other segments fade so the selected bucket stands out.
  selected?: Sentiment | null;
  // When given, each segment becomes a button that selects its bucket.
  onSelect?: (sentiment: Sentiment) => void;
};

/**
 * The unhappy / neutral / happy split of a topic's classified questions, each
 * segment as wide as its count. Shared by the topic cards and the View modal.
 */
export const SentimentSplitBar: React.FC<SentimentSplitBarProps> = ({
  sentiment,
  selected = null,
  onSelect,
}) => {
  const { t } = useTranslation();

  if (sentiment.total === 0) {
    return (
      <span className='intelligence__no-data text-tiny'>
        {t('general.no-data')}
      </span>
    );
  }

  return (
    <div className='intelligence__split-bar'>
      {SENTIMENT_VALUES.filter((level) => sentiment[level] > 0).map((level) => {
        const isFaded = selected !== null && selected !== level;
        const segmentClassName = `intelligence__split-segment ${
          SENTIMENT_DISPLAY[level].barColorClass
        }${isFaded ? ' opacity-30' : ''}`;
        const segmentStyle = { flexGrow: sentiment[level] };
        const countLabel = t(`home.v2.intelligence.cards.count.${level}`, {
          count: sentiment[level],
        });

        if (!onSelect) {
          return (
            <span
              key={level}
              className={segmentClassName}
              style={segmentStyle}
            />
          );
        }

        return (
          <button
            key={level}
            type='button'
            aria-label={countLabel}
            aria-pressed={selected === level}
            onClick={() => onSelect(level)}
            className={`${segmentClassName} ${SENTIMENT_DISPLAY[level].barHoverColorClass} cursor-pointer`}
            style={segmentStyle}
          />
        );
      })}
    </div>
  );
};

type SentimentCountsProps = {
  sentiment: SentimentBuckets;
  selected?: Sentiment | null;
  onSelect?: (sentiment: Sentiment) => void;
};

const resolveCountClassName = (
  level: Sentiment,
  selected: Sentiment | null,
): string => {
  if (selected === null) {
    return 'text-gray-400';
  }
  if (selected === level) {
    return 'font-semibold text-gray-700 underline underline-offset-2';
  }
  return 'text-gray-300';
};

// The printed counts under a split bar, each led by a dot in its segment's colour -
// so no separate legend is needed. Empty buckets are left out, like their bar
// segments. Clickable in the View modal.
export const SentimentCounts: React.FC<SentimentCountsProps> = ({
  sentiment,
  selected = null,
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <div className='intelligence__split-counts'>
      {SENTIMENT_VALUES.filter((level) => sentiment[level] > 0).map((level) => {
        const label = t(`home.v2.intelligence.cards.count.${level}`, {
          count: sentiment[level],
        });
        const className = `intelligence__split-count ${resolveCountClassName(
          level,
          selected,
        )}`;
        // * The dot ties the count to its bar segment and fades with it.
        const isFaded = selected !== null && selected !== level;
        const content = (
          <>
            <span
              className={`intelligence__split-count-dot ${
                SENTIMENT_DISPLAY[level].barColorClass
              }${isFaded ? ' opacity-30' : ''}`}
            />
            {label}
          </>
        );

        if (!onSelect) {
          return (
            <span key={level} className={className}>
              {content}
            </span>
          );
        }

        return (
          <button
            key={level}
            type='button'
            aria-pressed={selected === level}
            onClick={() => onSelect(level)}
            className={`${className} cursor-pointer hover:text-gray-700`}>
            {content}
          </button>
        );
      })}
    </div>
  );
};
